/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  LocalShipping as ShippingIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons';

import { Skeleton } from '@mui/material';
import { FormatDate, FormatMoney } from 'components';
import { EmptyState, MetricsCard, STATUS_LABELS, StatusChip } from 'components/Contigo';
import { contigoService } from 'services';

import ShippingTab from './components/ShippingTab';
import { useStyles } from './styles';

function TabPanel({ children, value, index }) {
  return (
    <div role='tabpanel' hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function OrdersList() {
  const classes = useStyles();
  const history = useHistory();

  const [currentTab, setCurrentTab] = useState(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    dateFilter: '',
    search: '',
  });

  useEffect(() => {
    loadOrders();
  }, [filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      const params = {
        page: 1,
        limit: 100,
      };

      if (filters.status) {
        params.status = filters.status;
      }

      const response = await contigoService.listOrders(params);
      let allOrders = response.data.orders || [];

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        allOrders = allOrders.filter(
          (order) =>
            order.order_number.toLowerCase().includes(searchLower) ||
            (order.samsung_order_number &&
              order.samsung_order_number.toLowerCase().includes(searchLower)) ||
            (order.qqn_number && order.qqn_number.toLowerCase().includes(searchLower)),
        );
      }

      setOrders(allOrders);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateOrder = () => {
    history.push('/contigo/orders/new');
  };

  const handleViewOrder = (orderId) => {
    history.push(`/contigo/orders/${orderId}`);
  };

  const handleEditOrder = (orderId) => {
    history.push(`/contigo/orders/${orderId}/edit`);
  };

  const handleFulfillOrder = (orderId) => {
    history.push(`/contigo/orders/${orderId}/fulfill`);
  };

  const calculateMetrics = () => {
    const allOrders = orders.filter((order) => order.status !== 'CANCELED');

    const totalOrders = allOrders.length;
    const totalValue = allOrders.reduce((sum, order) => sum + (order.total_sell || 0), 0);
    const avgOrderValue = totalOrders > 0 ? totalValue / totalOrders : 0;

    const statusCount = {
      pending: allOrders.filter((o) => o.status === 'PENDING').length,
      inProgress: allOrders.filter((o) =>
        ['CONFIRMED', 'IN_FULFILLMENT', 'PARTIALLY_FULFILLED'].includes(o.status),
      ).length,
      fulfilled: allOrders.filter((o) => o.status === 'FULFILLED').length,
      invoiced: allOrders.filter((o) => ['PARTIALLY_INVOICED', 'INVOICED'].includes(o.status))
        .length,
      completed: allOrders.filter((o) => o.status === 'COMPLETED').length,
    };

    const totalItems = allOrders.reduce((sum, order) => {
      const orderItems = order.items?.length || 0;
      return sum + orderItems;
    }, 0);

    const conversionRate =
      totalOrders > 0 ? ((statusCount.invoiced + statusCount.completed) / totalOrders) * 100 : 0;

    return {
      totalOrders,
      totalValue,
      avgOrderValue,
      statusCount,
      totalItems,
      conversionRate,
    };
  };

  const metrics = calculateMetrics();

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant='h4' className={classes.title}>
          Gestão Contigo
        </Typography>
        <Box style={{ display: 'flex', gap: 12 }}>
          <Button
            variant='outlined'
            color='primary'
            startIcon={<ReceiptIcon />}
            onClick={() => history.push('/contigo/invoice')}
            style={{ minWidth: 150 }}
          >
            Faturar Pedidos
          </Button>
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={handleCreateOrder}
            className={classes.createButton}
          >
            Novo Pedido
          </Button>
        </Box>
      </Box>

      {/* Dashboard de Métricas */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            label='Total de Pedidos'
            value={metrics.totalOrders}
            subtitle={`${metrics.totalItems} itens`}
            icon={<ShoppingCartIcon />}
            color='#1565C0'
            backgroundColor='#E3F2FD'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            label='Valor Total'
            value={<FormatMoney>{metrics.totalValue}</FormatMoney>}
            subtitle={`Média: ${Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(metrics.avgOrderValue)}`}
            icon={<AttachMoneyIcon />}
            color='#2E7D32'
            backgroundColor='#E8F5E9'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            label='Em Andamento'
            value={metrics.statusCount.inProgress}
            subtitle='Atendimento'
            icon={<ShippingIcon />}
            color='#7B1FA2'
            backgroundColor='#F3E5F5'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            label='Taxa de Conclusão'
            value={`${metrics.conversionRate.toFixed(1)}%`}
            subtitle={`${metrics.statusCount.invoiced + metrics.statusCount.completed} faturados`}
            icon={<CheckCircleIcon />}
            color='#00695C'
            backgroundColor='#E0F2F1'
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card style={{ marginBottom: 24 }}>
        <AppBar position='static' color='default' elevation={0}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            indicatorColor='primary'
            textColor='primary'
            variant='fullWidth'
          >
            <Tab label='Pedidos' icon={<ShoppingCartIcon />} />
            <Tab label='Expedição' icon={<ShippingIcon />} />
          </Tabs>
        </AppBar>

        {/* Tab 1: Pedidos */}
        <TabPanel value={currentTab} index={0}>
          <CardContent>
            {/* Filtros */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder='Buscar por número ou cliente...'
                  variant='outlined'
                  size='small'
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label='Status'
                  variant='outlined'
                  size='small'
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value=''>Todos</MenuItem>
                  {Object.keys(STATUS_LABELS).map((status) => (
                    <MenuItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label='Período'
                  variant='outlined'
                  size='small'
                  value={filters.dateFilter}
                  onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
                >
                  <MenuItem value=''>Todos</MenuItem>
                  <MenuItem value='TODAY'>Hoje</MenuItem>
                  <MenuItem value='WEEK'>Esta Semana</MenuItem>
                  <MenuItem value='MONTH'>Este Mês</MenuItem>
                  <MenuItem value='LAST_MONTH'>Mês Passado</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Lista de Pedidos */}
            {loading ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          mb={2}
                        >
                          <Skeleton variant='text' width='50%' height={32} />
                          <Skeleton
                            variant='rectangular'
                            width={90}
                            height={24}
                            sx={{ borderRadius: 4 }}
                          />
                        </Box>
                        <Skeleton variant='text' width='40%' height={20} />
                        <Skeleton
                          variant='text'
                          width='70%'
                          height={24}
                          style={{ marginBottom: 8 }}
                        />
                        <Skeleton
                          variant='text'
                          width='60%'
                          height={20}
                          style={{ marginBottom: 16 }}
                        />
                        <Skeleton variant='text' width='80%' height={20} />
                        <Skeleton
                          variant='text'
                          width='75%'
                          height={28}
                          style={{ marginBottom: 8 }}
                        />
                        <Skeleton variant='text' width='65%' height={20} />
                        <Box display='flex' gap={1} mt={2}>
                          <Skeleton variant='circular' width={32} height={32} />
                          <Skeleton variant='circular' width={32} height={32} />
                          <Skeleton variant='circular' width={32} height={32} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : orders.length === 0 ? (
              <Card className={classes.emptyCard}>
                <CardContent className={classes.emptyContent}>
                  <EmptyState
                    title='Nenhum pedido encontrado'
                    subtitle='Crie seu primeiro pedido clicando no botão acima'
                  />
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {orders.map((order) => (
                  <Grid item xs={12} md={6} lg={4} key={order.id}>
                    <Card className={classes.orderCard} elevation={2}>
                      <CardContent>
                        {/* Header do Card */}
                        <Box className={classes.cardHeader}>
                          <Typography variant='h6' className={classes.orderNumber}>
                            {order.order_number}
                          </Typography>
                          <StatusChip status={order.status} />
                        </Box>

                        {/* Informações do Cliente */}
                        <Box className={classes.customerInfo}>
                          <Typography variant='body2' color='textSecondary'>
                            Cliente
                          </Typography>
                          <Typography variant='body1' className={classes.customerName}>
                            {order.customer?.NOME || 'Cliente não informado'}
                          </Typography>
                          {order.customer?.APELIDO && (
                            <Typography variant='body2' color='textSecondary'>
                              {order.customer.APELIDO}
                            </Typography>
                          )}
                        </Box>

                        {/* Informações do Pedido */}
                        <Box className={classes.orderInfo}>
                          <Box className={classes.infoRow}>
                            <Typography variant='body2' color='textSecondary'>
                              Itens:
                            </Typography>
                            <Chip label={order.items?.length || 0} size='small' color='default' />
                          </Box>
                          <Box className={classes.infoRow}>
                            <Typography variant='body2' color='textSecondary'>
                              Valor Total:
                            </Typography>
                            <Typography variant='h6' className={classes.totalValue}>
                              <FormatMoney>{order.total_sell || 0}</FormatMoney>
                            </Typography>
                          </Box>
                          <Box className={classes.infoRow}>
                            <Typography variant='body2' color='textSecondary'>
                              Criado em:
                            </Typography>
                            <Typography variant='body2'>
                              <FormatDate>{order.created_at}</FormatDate>
                            </Typography>
                          </Box>
                        </Box>

                        {/* Ações */}
                        <Box className={classes.actions}>
                          <Tooltip title='Visualizar'>
                            <IconButton
                              size='small'
                              onClick={() => handleViewOrder(order.id)}
                              color='primary'
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          {[
                            'AWAITING_DELIVERY',
                            'IN_FULFILLMENT',
                            'PARTIALLY_FULFILLED',
                            'FULFILLED',
                            'PARTIALLY_INVOICED',
                            'INVOICED',
                          ].includes(order.status) && (
                            <Tooltip title='Atender Pedido'>
                              <IconButton
                                size='small'
                                onClick={() => handleFulfillOrder(order.id)}
                                style={{ color: '#7B1FA2' }}
                              >
                                <ShippingIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </TabPanel>

        {/* Tab 2: Expedição */}
        <TabPanel value={currentTab} index={1}>
          <ShippingTab />
        </TabPanel>
      </Card>
    </Box>
  );
}
